import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import useSWR, { mutate } from 'swr';
import { Permissions } from '../../config';

import { Add, AddAlert, Category } from '@mui/icons-material';
import { Skeleton, Table, TableHead, TableBody, TableRow, TableCell, IconButton, Tooltip } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import SparaFilter from 'components/SparaFilter/SparaFilter';
import SnackbarCustom from 'components/Snackbar/Snackbar.js';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { fetcher, companyCategoryApi } from '../../lib/api';
import AddCategory from './addCategory';
import styles from './category.module.scss';

const SKELETON_COUNT = 6;

const RowSkeleton = () => (
    <TableRow>
        <TableCell><Skeleton variant="rounded" width={36} height={36} /></TableCell>
        <TableCell>
            <Skeleton variant="text" width={120} height={18} />
            <Skeleton variant="text" width={90} height={14} sx={{ mt: 0.3 }} />
        </TableCell>
        <TableCell><Skeleton variant="rounded" width={40} height={22} sx={{ borderRadius: 20 }} /></TableCell>
        <TableCell align="right">
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
            </div>
        </TableCell>
    </TableRow>
);

const Categories = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
    const [search, setSearch] = useState('');
    const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });

    const userPermission = JSON.parse(localStorage.getItem('user')).permission;
    const canCreate = userPermission.includes(Permissions.CATEGORY_CREATE);
    const canUpdate = userPermission.includes(Permissions.CATEGORY_UPDATE);
    const canDelete = userPermission.includes(Permissions.CATEGORY_DELETE);

    const { data, isLoading } = useSWR(companyCategoryApi.list, fetcher);
    const categories = data?.results || [];

    const filtered = search.trim()
        ? categories.filter((c) =>
            c.title?.toLowerCase().includes(search.toLowerCase()) ||
            c.title_en?.toLowerCase().includes(search.toLowerCase())
        )
        : categories;

    const showNotification = (message, type = 'success') => {
        setNotification({ open: true, message, type });
        setTimeout(() => setNotification((prev) => ({ ...prev, open: false })), 4000);
    };

    const handleSubmit = async (formData, isUpdate) => {
        try {
            await fetcher(
                isUpdate ? companyCategoryApi.update(formData.id) : companyCategoryApi.create,
                isUpdate ? 'PUT' : 'POST',
                formData
            );
            mutate(companyCategoryApi.list);
            showNotification(t('common.saved_successfully'), 'success');
        } catch {
            showNotification(t('common.error_occurred'), 'warning');
        }
    };

    const handleDelete = (category) => {
        setDeleteDialog({ open: true, category });
    };

    const confirmDelete = async () => {
        const { category } = deleteDialog;
        setDeleteDialog({ open: false, category: null });
        try {
            await fetcher(companyCategoryApi.delete(category.id), 'DELETE');
            mutate(companyCategoryApi.list);
            showNotification(t('common.deleted_successfully'), 'success');
        } catch {
            showNotification(t('common.error_occurred'), 'warning');
        }
    };

    const openForm = (data = {}) => {
        setAlert(
            <AddCategory
                classes={{}}
                data={data}
                onCancel={() => setAlert(null)}
                onSubmit={(formData, isUpdate) => {
                    handleSubmit(formData, isUpdate);
                    setAlert(null);
                }}
            />
        );
    };

    return (
        <GridContainer>
            {alert && createPortal(alert, document.body)}
            <ConfirmDialog
                open={deleteDialog.open}
                type="danger"
                title={t('are_you_sure')}
                message={t('delete_confirmation')}
                confirmText={t('delete_yes')}
                cancelText={t('cancel')}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteDialog({ open: false, category: null })}
            />
            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={notification.type === 'success' ? 'success' : 'warning'}
                message={notification.message}
                open={notification.open}
                closeNotification={() => setNotification((prev) => ({ ...prev, open: false }))}
            />
            <GridItem xs={12}>
                <SparaTitle
                    title={t('menu.categories')}
                    rightComponent={
                        canCreate && (
                            <SparaButton color="primary" icon={Add} onClick={() => openForm()}>
                                {t('category.add_new')}
                            </SparaButton>
                        )
                    }
                />
                <Card style={{ margin: '0px' }}>
                    <CardBody>
                        <SparaFilter
                            filters={{}}
                            filterGroups={[]}
                            onFilterClick={() => {}}
                            onClearAll={() => setSearch('')}
                            search={search}
                            onSearchChange={(e) => setSearch(e?.target?.value ?? e)}
                        />

                        <div className={styles.listHeader}>
                            {!isLoading && (
                                <span className={styles.listHeaderCount}>
                                    {t('filter.showing')} {filtered.length} {t('filter.of')} {categories.length} {t('menu.categories').toLowerCase()}
                                </span>
                            )}
                        </div>

                        <div className={styles.tableWrap}>
                            <Table size="small" sx={{ width: '100%' }}>
                                <TableHead>
                                    <TableRow className={styles.tableHead}>
                                        <TableCell>{t('icon')}</TableCell>
                                        <TableCell>{t('name')}</TableCell>
                                        <TableCell>{t('order')}</TableCell>
                                        <TableCell align="right">{t('actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading
                                        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                                            <RowSkeleton key={i} />
                                        ))
                                        : filtered.map((cat) => (
                                            <TableRow key={cat.id} className={styles.tableRow}>
                                                <TableCell>
                                                    {cat.logo
                                                        ? <img src={cat.logo} alt={cat.title} className={styles.categoryIcon} />
                                                        : <div className={styles.iconPlaceholder}><Category fontSize="small" /></div>
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <p className={styles.categoryName}>{cat.title}</p>
                                                    <p className={styles.categoryNameEn}>{cat.title_en}</p>
                                                </TableCell>
                                                <TableCell>
                                                    <span className={styles.sortBadge}>{cat.sort_order ?? '—'}</span>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                                        {canUpdate && (
                                                            <Tooltip title={t('edit')}>
                                                                <IconButton size="small" onClick={() => openForm(cat)}>
                                                                    <Edit sx={{ fontSize: 17 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {canDelete && (
                                                            <Tooltip title={t('delete')}>
                                                                <IconButton size="small" color="error" onClick={() => handleDelete(cat)}>
                                                                    <Delete sx={{ fontSize: 17 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </div>
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default Categories;
